import dayjs from "dayjs";

export const useAttendanceChartData = (
  analyticsReport = [],
  trendReport = []
) => {
  /**
   * ---------------------------------------------------
   * 1) SUMMARY STACKED BAR + PIE
   *    Uses NEW attendance analytics API
   * ---------------------------------------------------
   */
  // Annual Leave and Casual Leave are the same underlying pool (labeled by
  // country), so they're summed into one "casual" field here rather than
  // plotted as two series — previously "annual" was computed but never
  // rendered by AttendanceCharts.js, so annual_leave days were silently
  // invisible on this chart for any institution using that type.
  // Compensatory Leave and Maternity Leave are paid, no-quota leave types
  // (same bucket as Leave With Pay — see hrpayroll.js evaluateDay), so
  // they're folded into leaveWithPay here rather than plotted separately.
  const barData = (analyticsReport || []).map((emp) => ({
    employee: emp.employee_name,
    present: Number(emp.present || 0),
    absent: Number(emp.absent || 0),
    sick:
      Number(emp.sick_leave || 0) + Number(emp.short_sick_leave || 0),
    casual:
      Number(emp.casual_leave || 0) +
      Number(emp.short_casual_leave || 0) +
      Number(emp.annual_leave || 0),
    leaveWithPay:
      Number(emp.leave_with_pay || 0) +
      Number(emp.compensatory_leave || 0) +
      Number(emp.maternity_leave || 0),
    leaveWithoutPay: Number(emp.leave_without_pay || 0),
    working_days: Number(emp.working_days || 0),
  }));

  const pieData = {};
  (analyticsReport || []).forEach((emp) => {
    pieData[emp.employee_id] = {
      empName: emp.employee_name,
      sick:
        Number(emp.sick_leave || 0) + Number(emp.short_sick_leave || 0),
      casual:
        Number(emp.casual_leave || 0) +
        Number(emp.short_casual_leave || 0) +
        Number(emp.annual_leave || 0),
      leaveWithPay:
        Number(emp.leave_with_pay || 0) +
        Number(emp.compensatory_leave || 0) +
        Number(emp.maternity_leave || 0),
      leaveWithoutPay: Number(emp.leave_without_pay || 0),
      absent: Number(emp.absent || 0),
      missing_dates: emp.missing_dates || [],
    };
  });

  /**
   * ---------------------------------------------------
   * 2) DAILY TREND LINE CHART
   *    Uses OLD attendance report API
   * ---------------------------------------------------
   *
   * trendReport contains row-wise attendance entries.
   * We aggregate per date:
   *   - present
   *   - leave
   *   - absent
   *
   * Assumption:
   * - old API already returns only approved/inprogress rows
   * - duplicates should already be handled in backend if needed
   */
  const dailyStatsMap = {};

  (trendReport || []).forEach((rec) => {
    if (!rec?.attendance_date) return;

    const date = dayjs(rec.attendance_date).format("YYYY-MM-DD");

    if (!dailyStatsMap[date]) {
      dailyStatsMap[date] = {
        date,
        present: 0,
        leave: 0,
        absent: 0,
      };
    }

    const attendance = rec.attendance;

    if (attendance === "present") {
      dailyStatsMap[date].present += 1;
      return;
    }

    if (
      [
        "sick_leave",
        "casual_leave",
        "annual_leave",
        "leave_with_pay",
        "leave_without_pay",
        "short_sick_leave",
        "short_casual_leave",
        "short_leave",
        "compensatory_leave",
        "maternity_leave",
      ].includes(attendance)
    ) {
      // Full leaves count as 1
      if (
        [
          "sick_leave",
          "casual_leave",
          "annual_leave",
          "leave_with_pay",
          "leave_without_pay",
          "compensatory_leave",
          "maternity_leave",
        ].includes(attendance)
      ) {
        dailyStatsMap[date].leave += 1;
      }

      // Short leaves count as 0.5
      if (["short_sick_leave", "short_casual_leave", "short_leave"].includes(attendance)) {
        dailyStatsMap[date].leave += 0.5;
      }

      return;
    }

    if (attendance === "absence") {
      dailyStatsMap[date].absent += 1;
    }
  });

  const lineData = Object.values(dailyStatsMap).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );

  return {
    barData,
    lineData,
    pieData,
  };
};